import { AfterViewInit, Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbstractComponent, Channel, ChannelEmbedded, ChannelService, EMBEDDED_SIGNUP_COEXISTENCE_FINISH } from 'lib-trend-core';
import { environment } from '../../../environments/environment';
import { facebookSdkFor, prepareFacebookSdk } from './facebook-sdk.util';

@Component({
    selector: 'app-whatsapp-signup',
    // templateUrl: './whatsapp-signup.component.html',
    templateUrl: './whatsapp-signup.v2.component.html',
    styleUrls: ['./whatsapp-signup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WhatsappSignupComponent extends AbstractComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() label: string = 'Start WhatsApp Signup';
  @Input() idChannel: string ;
  @Input() nameChannel: string;
  @Input() disabledButton: boolean;
  @Input() channelFormGroup: FormGroup;

  /**
   * The channel runs on the Solution Partner's credit line: the signup goes through the
   * credit-line Meta app (environment.cloudAPICreditLine) instead of the regular one.
   */
  @Input() creditLine: boolean = false;

  /**
   * The channel is Hybrid (SPEC-2026-10-whatsapp-hybrid-channel RF-02, P-1): only the coexistence
   * onboarding is accepted, because the number must stay on the WhatsApp Business app for the
   * session side to pair by QR. A classic FINISH is refused here and again by the backend.
   */
  @Input() hybrid: boolean = false;

  /** Translation key of the idle label — the reconnect action passes the "Reconectar" wording. */
  @Input() labelKey: string = 'whatsappSignup.home.connectButton';

  /** Button classes. The default is the large call to action used when connecting a channel. */
  @Input() buttonClass: string = 'btn btn-primary rounded-5 whatsapp-signup__button';

  @Output() onSave: EventEmitter<Channel> = new EventEmitter<Channel>();
  @Output() onClick: EventEmitter<Channel> = new EventEmitter<Channel>();

  labelButton = 'Conectar com WhatsApp Embedded';

  /** Embedded Signup session info (FINISH* events) — arrives via the message event before the FB.login callback. */
  private sessionInfo: any = null;

  /** OAuth code. On v4 it comes back in the FB.login callback (~30s TTL); the ?code= message is the legacy fallback. */
  private oauthCode: string | null = null;

  /** Prevents double submission when the code arrives through more than one path (callback + legacy message). */
  private submitted = false;

  /** Fallback timer: the flow finished but the code never arrived — submit without it (default token). */
  private codeWaitTimer: any = null;

  constructor(
    injector: Injector,
    public channelService: ChannelService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loading = false;
    this.labelButton = this.t(this.labelKey);
    window.addEventListener('message', this.handleMessageEvent);
  }

  override ngOnDestroy(): void {
    // Clean up event listener to avoid memory leaks
    window.removeEventListener('message', this.handleMessageEvent);
    clearTimeout(this.codeWaitTimer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The channel (and so the Meta app) can arrive after the first render.
    if (changes['creditLine'] && !changes['creditLine'].firstChange) {
      this.prepareSdk();
    }
  }

  ngAfterViewInit() {
    this.loadWhatsappSignup();
  }

  /** The Meta app this channel is onboarded through — regular Cloud API or credit line. */
  private get metaApp(): { appId: string; configId: string; solutionId?: string } {
    return this.creditLine ? environment.cloudAPICreditLine : environment.cloudAPI;
  }

  private get isMetaAppConfigured(): boolean {
    return !!this.metaApp?.appId && !!this.metaApp?.configId;
  }

  /** Loads the SDK ahead of the click, so FB.login can run synchronously (popup blockers). */
  private prepareSdk(): void {
    if (!this.isMetaAppConfigured) {
      return;
    }
    prepareFacebookSdk(this.metaApp.appId).catch((error) => console.error('Facebook SDK:', error));
  }

  loadWhatsappSignup() {
    if (this.channelFormGroup && !this.channelFormGroup.valid) {
      this.validateAllFormFields(this.channelFormGroup);
      return;
    };
    this.prepareSdk();
  }

  launchWhatsAppSignup() {
    if (!this.isMetaAppConfigured) {
      this.alertService.error(this.t('whatsappSignup.home.creditLineAppNotConfigured'));
      return;
    }
    const FB = facebookSdkFor(this.metaApp.appId);
    if (!FB) {
      // Still loading (or blocked): start loading and let the user click again — calling FB.login
      // after an await would lose the user gesture and get the popup blocked.
      this.prepareSdk();
      this.alertService.error(this.t('whatsappSignup.home.sdkUnavailable'));
      return;
    }
    this.loading = true;
    this.submitted = false;
    this.sessionInfo = null;
    this.oauthCode = null;
    this.labelButton = this.t('whatsappSignup.home.configuringBusinessManager');
    FB.login(this.fbLoginCallback, {
      // v4 configuration: Facebook Login for Business > Configurations, "Embedded Signup" login
      // variation with the desired products (selecting products is what activates v4).
      config_id: this.metaApp.configId,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        // Credit-line channels carry the solution linked to the partner's credit line, when the
        // environment defines one; regular channels keep an empty setup.
        setup: this.metaApp.solutionId ? { solutionID: this.metaApp.solutionId } : {},
        // Coexistence (WhatsApp Business app onboarding) — still supported on v4.
        featureType: 'whatsapp_business_app_onboarding',
      }
    });
  }

  fbLoginCallback = (response) => {
    if (response?.authResponse?.code) {
      // v4: the exchangeable code (~30s TTL) comes back here — the backend exchanges it for a token right away.
      this.oauthCode = response.authResponse.code;
      this.trySubmit();
    } else if (this.sessionInfo && !this.submitted) {
      // Flow finished but the callback came without a code (rare): the classic flow proceeds
      // with the backend's default token; coexistence cannot proceed without a code.
      this.trySubmit(true);
    } else {
      // Popup closed or authorization denied — unlock the button for another attempt.
      this.resetButton();
    }
  }

  private handleMessageEvent = (event: MessageEvent) => {
    const origin = event.origin || '';
    // Accepts any facebook.com subdomain (www, web, business) — dot-anchored.
    if (origin !== 'https://facebook.com' && !origin.endsWith('.facebook.com')) {
      return;
    }
    try {
      if (typeof event.data === 'string') {
        const match = event.data.match(/[\?&]code=([^&]+)/);
        if (match) {
          // Legacy fallback (v2): the code used to arrive in a redirect message.
          this.oauthCode = match[1];
          this.trySubmit();
          return;
        }
      }
      const data = JSON.parse(event.data);
      if (data.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }
      switch (data.event) {
        case 'FINISH':
        case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING':
          this.sessionInfo = data;
          this.trySubmit();
          // Fallback: if the FB.login callback does not deliver the code within a few seconds,
          // the classic flow proceeds with the default token (the code has a ~30s TTL, so waiting
          // indefinitely is not an option); coexistence is unlocked with an error.
          if (!this.submitted) {
            clearTimeout(this.codeWaitTimer);
            this.codeWaitTimer = setTimeout(() => this.trySubmit(true), 15000);
          }
          break;
        case 'FINISH_ONLY_WABA':
          // v4 allows finishing without adding/verifying a phone — without a phone_number_id there is no channel.
          this.resetButton();
          this.alertService.error(this.t('whatsappSignup.home.finishedWithoutPhone'));
          break;
        case 'CANCEL':
        case 'ERROR':
          // CANCEL carries current_step (abandonment) or error_message (an error reported by Meta).
          this.resetButton();
          if (data.data?.error_message) {
            this.alertService.error(`${this.t('whatsappSignup.home.metaError')} ${data.data.error_message}`);
          }
          break;
      }
    } catch {
      console.error('message event (non-JSON): ', event.data);
    }
  };

  /**
   * Submits to the backend as soon as the required parts arrive, in whatever order they come.
   * Every flow waits for the `code` from the FB.login callback: with it the backend exchanges
   * a customer-owned business integration token for the channel. The classic flow (FINISH) may
   * proceed without a code (`force`, via the fallback) using the backend's default token;
   * coexistence has no phone_number_id and cannot connect without a code. `submitted` prevents
   * duplicate submission.
   */
  private trySubmit(force: boolean = false): void {
    if (this.submitted || !this.sessionInfo) {
      return;
    }
    const isCoexistence = this.sessionInfo.event === EMBEDDED_SIGNUP_COEXISTENCE_FINISH;
    if (this.hybrid && !isCoexistence) {
      this.resetButton();
      this.alertService.error(this.t('whatsappSignup.home.hybridRequiresCoexistence'));
      return;
    }
    if (!this.oauthCode) {
      if (!force) {
        return; // wait for the code from the FB.login callback (or from the legacy message)
      }
      if (isCoexistence || this.creditLine) {
        // Coexistence has no phone_number_id, and the backend's default token belongs to the
        // regular Meta app — neither can connect without a code.
        console.error('Embedded Signup: finished without a code — the channel cannot be connected.');
        this.resetButton();
        return;
      }
    }
    const { business_id, waba_id, phone_number_id } = this.sessionInfo.data || {};
    if (!isCoexistence && !phone_number_id) {
      // FINISH without a phone should not happen, but this protects the backend from an incomplete payload.
      this.resetButton();
      this.alertService.error(this.t('whatsappSignup.home.finishedWithoutPhone'));
      return;
    }
    this.submitted = true;
    clearTimeout(this.codeWaitTimer);
    this.labelButton = this.t('whatsappSignup.home.finalizingBusinessConfig');
    const channelEmbedded = <ChannelEmbedded>{
      name: this.nameChannel || 'WhatsApp Channel Integration',
      whatsAppBusinessAccountID: String(waba_id),
      finishEvent: this.sessionInfo.event,
      // With a code this channel gets its own customer token; without one, the backend uses the default.
      ...(this.oauthCode ? { code: this.oauthCode } : {}),
      ...(isCoexistence
        ? {
          businessID: business_id != null ? String(business_id) : undefined,
        }
        : {
          phoneNumberID: String(phone_number_id),
        }),
    };
    this.channelService.createFromEmbedded(this.idChannel, channelEmbedded).subscribe({
      next: (channel: Channel) => {
        this.resetButton();
        this.alertService.success(this.t('whatsappSignup.home.channelConnectedSuccess'));
        this.onSave.emit(channel);
      },
      // Restores the button: it stayed on "Finalizando..." after a failure, with no way to try
      // again other than reloading the page.
      error: (err) => {
        this.submitted = false;
        this.resetButton();
        this.alertService.error(err.error?.message);
      }
    });
  }

  private resetButton(): void {
    this.loading = false;
    this.labelButton = this.t(this.labelKey);
  }
}
